import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { findInsight, INSIGHTS, type Insight } from "@/lib/insights";

export const Route = createFileRoute("/learn/$slug")({
  loader: ({ params }) => {
    const insight = findInsight(params.slug);
    if (!insight) throw notFound();
    return insight;
  },
  head: ({ params, loaderData }) => {
    const insight = loaderData ?? findInsight(params.slug);
    if (!insight) return { meta: [{ title: "Not found" }] };
    const kind = insight.type === "tip" ? "Gold Tip" : "Gold Lesson";
    return {
      meta: [
        { title: `${insight.title} — ${kind}` },
        { name: "description", content: insight.body.slice(0, 155) },
        { property: "og:title", content: insight.title },
        { property: "og:description", content: insight.body.slice(0, 155) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `/learn/${insight.slug}` },
      ],
      links: [{ rel: "canonical", href: `/learn/${insight.slug}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: insight.title,
            description: insight.body,
            author: { "@type": "Organization", name: "DrHint" },
          }),
        },
      ],
    };
  },
  component: LearnPage,
  notFoundComponent: () => (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="text-2xl font-bold">Not found</h1>
        <Link to="/learn" className="mt-3 inline-block text-primary underline">Back to all lessons</Link>
      </div>
    </Layout>
  ),
  errorComponent: ({ error }) => (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="text-2xl font-bold">Error</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
      </div>
    </Layout>
  ),
});

function LearnPage() {
  const insight = Route.useLoaderData() as Insight;
  const related = INSIGHTS.filter((i) => i.slug !== insight.slug).slice(0, 6);

  return (
    <Layout>
      <article className="mx-auto max-w-3xl px-4 py-10">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link> ›{" "}
          <Link to="/learn" className="hover:underline">Learn</Link>
        </nav>
        <div className="text-xs uppercase tracking-wider text-amber-600 font-semibold">
          {insight.type === "tip" ? "Quick gold tip" : "60-second lesson"}
        </div>
        <h1 className="mt-2 text-3xl md:text-4xl font-bold leading-tight">{insight.title}</h1>
        <p className="mt-5 text-lg text-foreground/85 leading-relaxed">{insight.body}</p>

        <div className="mt-10 rounded-xl border border-border bg-muted/40 p-5">
          <div className="font-semibold mb-1">See today's live gold rate</div>
          <p className="text-sm text-muted-foreground mb-3">
            Apply this tip to today's actual 24K, 22K and 21K prices.
          </p>
          <div className="flex gap-2">
            <Link to="/pakistan" className="rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground">Pakistan rates</Link>
            <Link to="/india" className="rounded-md border border-border bg-card px-3 py-2 text-xs font-semibold">India rates</Link>
          </div>
        </div>

        <div className="mt-10">
          <h2 className="text-xl font-bold mb-3">More gold tips & lessons</h2>
          <ul className="space-y-2">
            {related.map((r) => (
              <li key={r.slug}>
                <Link to="/learn/$slug" params={{ slug: r.slug }} className="text-primary underline">
                  {r.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </article>
    </Layout>
  );
}